import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { SERVICES } from "../data/mock";
import { getIcon } from "../lib/icons";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import NotFound from "./NotFound";

const ServiceDetail = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = translations[lang];
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <NotFound />;

  const Icon = getIcon(service.icon);
  const others = SERVICES.filter((s) => s.id !== service.id);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title[lang],
    description: service.short[lang],
    provider: { "@type": "Organization", name: "AdalCrea" },
    areaServed: "TR",
  };

  return (
    <>
      <Seo
        title={service.title[lang]}
        description={service.short[lang]}
        path={`/hizmetlerimiz/${service.slug}`}
        lang={lang}
        jsonLd={jsonLd}
      />
      <PageHero title={service.title[lang]} desc={service.short[lang]}>
        <Link
          to="/hizmetlerimiz"
          className="mt-6 inline-flex items-center gap-2 text-amber-400 font-semibold hover:gap-3 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.nav.services}
        </Link>
      </PageHero>

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden mb-10">
              <img
                src={service.image}
                alt={service.title[lang]}
                className="w-full h-[360px] object-cover"
              />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
                <Icon className="w-6 h-6 text-amber-600" />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wider text-amber-500">
                {service.tag[lang]}
              </span>
            </div>
            <p className="text-lg text-slate-700 leading-relaxed">{service.short[lang]}</p>

            <h3 className="mt-10 font-display font-bold text-xl text-navy-900">
              {t.servicesPage.includes}
            </h3>
            <ul className="mt-5 grid sm:grid-cols-2 gap-4">
              {service.features[lang].map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-amber-500/50 transition-colors"
                >
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-amber-600" />
                  </span>
                  <span className="text-slate-700 text-[15px]">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-2xl bg-navy-900 p-8">
                <h3 className="font-display font-bold text-xl text-white">
                  {t.ctaBanner.title}
                </h3>
                <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                  {t.ctaBanner.desc}
                </p>
                <Link
                  to="/iletisim"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-5 py-3 rounded-lg transition-colors"
                >
                  {t.ctaBanner.button}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="rounded-2xl border border-slate-200 p-6">
                <h4 className="font-display font-semibold text-navy-900 mb-4">
                  {t.nav.services}
                </h4>
                <ul className="space-y-2">
                  {others.map((o) => (
                    <li key={o.id}>
                      <Link
                        to={`/hizmetlerimiz/${o.slug}`}
                        className="flex items-center justify-between text-sm text-slate-600 hover:text-amber-600 py-2 border-b border-slate-100 last:border-0 transition-colors"
                      >
                        {o.title[lang]}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
